import { createAsyncThunk } from '@reduxjs/toolkit'
import { ServiceConfig } from '../services/config'
import { APIError, get, post, put, remove } from '../services/crud'
import {
  IKnockStatus,
  KnockStatusDetails,
  KnockStatusHistoryInput,
  KnockStatusHistoryRecord,
  KnockStatusInput,
  KnockStatusLocationBaseInput,
  UpdateKnockStatusHistoryInput,
} from './typings'

const baseUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.knockStatus.knockStatus}`
const historyUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.knockStatus.history}`
const locationsUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.knockStatus.locations}`
const contacLocationUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.knockStatus.contactLocations}`
const locationsUrlNew = `${ServiceConfig.baseAPIUrl}${ServiceConfig.knockStatus.locationsNew}`

export const getAllKnockStatuses = createAsyncThunk<
  IKnockStatus[],
  void,
  { rejectValue: APIError }
>('knockStatus/getAll', async (_, thunkApi) => {
  const response = await get(baseUrl)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getKnockStatus = createAsyncThunk<
  IKnockStatus,
  string,
  { rejectValue: APIError }
>('knockStatus/get', async (id, thunkApi) => {
  const response = await get(`${baseUrl}/${id}`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const createKnockStatus = createAsyncThunk<
  IKnockStatus,
  KnockStatusInput,
  { rejectValue: APIError }
>('knockStatus/create', async (input, thunkApi) => {
  const response = await post(baseUrl, input)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const updateKnockStatus = createAsyncThunk<
  IKnockStatus,
  KnockStatusInput,
  { rejectValue: APIError }
>('knockStatus/update', async (input, thunkApi) => {
  const response = await put(baseUrl, input)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const deleteKnockStatus = createAsyncThunk<
  string,
  string,
  { rejectValue: APIError }
>('knockStatus/delete', async (id, thunkApi) => {
  const response = await remove(`${baseUrl}/${id}`)
  if (response.ok) return id
  return thunkApi.rejectWithValue(await response.json())
})

export const createKnockStatusLocation = createAsyncThunk<
  KnockStatusDetails,
  KnockStatusHistoryInput,
  { rejectValue: APIError }
>('knockStatusHistory/create', async (input, thunkApi) => {
  const response = await post(locationsUrl, input)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getKnockStatusHistoryByLocation = createAsyncThunk<
  KnockStatusHistoryRecord[],
  number,
  { rejectValue: APIError }
>('knockStatusHistory/getByEntity', async (knockStatusLocationId, thunkApi) => {
  const response = await get(`${locationsUrl}/${knockStatusLocationId}/history`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getKnockStatusHistoryByContactLocation = createAsyncThunk<
  KnockStatusHistoryRecord[],
  number,
  { rejectValue: APIError }
>(
  'knockStatusHistory/getByContactLocation',
  async (knockStatusContactLocationId, thunkApi) => {
    const response = await get(
      `${contacLocationUrl}/${knockStatusContactLocationId}/history`,
    )
    if (response.ok) return await response.json()
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const updateKnockStatusHistoryById = createAsyncThunk<
  KnockStatusHistoryRecord,
  { id: number; entityType: string; input: UpdateKnockStatusHistoryInput },
  { rejectValue: APIError }
>('knockStatusHistory/update', async ({ id, entityType, input }, thunkApi) => {
  const response = await put(
    `${historyUrl}/${id}?entityType=${entityType}`,
    input,
  )
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const addKnockStatusHistory = createAsyncThunk<
  KnockStatusDetails,
  KnockStatusHistoryInput,
  { rejectValue: APIError }
>(
  'knockStatusHistory/addHistory',
  async (input: KnockStatusHistoryInput, thunkApi) => {
    const response = await put(`${locationsUrl}`, input)
    if (response.ok) return await response.json()
    return thunkApi.rejectWithValue(await response.json())
  },
)

export const createKnockStatusLocationBase = createAsyncThunk<
  KnockStatusLocationBaseInput,
  KnockStatusLocationBaseInput,
  { rejectValue: APIError }
>('KnockStatusLocations/CreateNewLocationPin', async (input, thunkApi) => {
  const response = await post(locationsUrlNew, input)
  if (response.ok) return response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const deleteKnockStatusLocation = createAsyncThunk<
  void,
  { locationId: number; entityType: string },
  { rejectValue: APIError }
>(
  'knockStatusLocations/delete',
  async ({ locationId, entityType }, thunkApi) => {
    const response = await remove(
      `${locationsUrl}/${locationId}/history?entityType=${entityType}`,
    )
    if (response.ok) return
    return thunkApi.rejectWithValue(await response.json())
  },
)

// SH-661 — clears the active knock status while keeping notes, timeline, and history.
export const clearCurrentKnockStatus = createAsyncThunk<
  void,
  { locationId: number; entityType: string },
  { rejectValue: APIError }
>(
  'knockStatusLocations/clearCurrent',
  async ({ locationId, entityType }, thunkApi) => {
    const response = await remove(
      `${locationsUrl}/${locationId}/current?entityType=${entityType}`,
    )
    if (response.ok) return
    return thunkApi.rejectWithValue(await response.json())
  },
)

// SH-1143 — getKnockStatusLocationByCoordinates was removed; the GET /KnockStatusLocations/byCoordinates
// endpoint was consolidated into GET /ContactLocations/recordsAtLocation (see store/map/api.ts
// getRecordsAtLocation), which returns the active knock-status info alongside the records at a coordinate.
